import { EmploymentTypeCode } from './enums/employment-type-code.enum';
import { OfferStatus } from './enums/offers.enum';
import { ListItem } from './list-item.model';

export interface GridData {
    data: GridDataItems[];
    total: number;
}

export interface GridDataItems {
    id: number;
    contractTypeId: number;
    contractType: string;
    candidateId: number;
    candidateName: string;
    facilityId: number;
    facilityName: string;
    professionId: number;
    expertiseId: number;
    expertise: string;
    startDate: string;
    accountManagerId: number;
    offerStatusId: number;
    offerStatus: string;
    profession: string;
    updatedDateUtc: string;
    contractRequestStatusId: number;
    contractRequestStatusText: string;
    contractRequestStatusUpdatedDate: number;
    contractRequestAssigneeFirstName: string | any;
    contractRequestAssigneeLastName: string | any;
    contractStageId: number;
    contractStageText: string;
    contractStageUpdatedDate: string;
    accountManagerName: string;
    facilityRate: number;
    agencyName?: string;
    jobId?: number;
    marginId?: number;
    useAyaAccountManagement?: boolean;
}

export interface DetailLookups {
    professions?: Professions[];
    jobIds?: number[];
    shiftTimes: ShiftTimes[];
    shiftTypes: ShiftTypes[];
    professionTypes: ProfessionTypes[];
    contracts: Contracts[];
    vendors?: Vendors[];
    facilities?: Facilities[];
}

export interface NewOfferDetailLookups {
    shiftTimes: ShiftTimes[];
    shiftTypes: ShiftTypes[];
    professionTypes: ProfessionTypes[];
    vendors: Vendors[];
    contracts: Contracts[];
}

export interface GridLookups {
    statuses: Statuses[];
    professions: ProfessionsLookups[];
    expertises: Expertise[];
    contractRequestStatuses?: ListItem[];
    contractStages?: ListItem[];
    contracts?: ContractLookups[];
    accountManagers?: ListItem[];
}

export interface ContractLookups extends ListItem {
    code: string;
}

export interface Statuses {
    id: number;
    name: string;
}

export interface Contracts {
    id: number;
    code: string;
    name: string;
}

export interface Facility {
    id: number;
    name: string;
}

export interface Professions {
    id: number;
    name: string;
    abbreviation: string;
    sortOrder: number;
    professionTypeId: number;
}

export interface ProfessionsProfLookup {
    id: number;
    name: string;
    abbreviation: string;
    sortOrder: number;
    professionTypeId: number;
}

export interface ProfessionsLookups {
    id: number;
    name: string;
    typeId: number;
    abbreviation: string;
    sort: number;
}

export interface VendorLookups {
    contacts: Contacts[];
    candidates: Candidates[];
    systems: Systems[];
    facilities: Facilities[];
}

export interface FacilityLookups {
    professions: ProfessionsProfLookup[];
    jobs: OfferJob[];
    otherDetails?: any;
    accountManager: string;
}

export interface ShiftTimes {
    key: number;
    value: string;
}

export interface ShiftTypes {
    key: number;
    value: string;
}

export interface Vendors {
    id: number;
    name: string;
    hasAcceptedFacilities: boolean;
}

export interface Contacts {
    id: number;
    name: string;
}

export interface Candidates {
    id: number;
    name: string;
    email: string;
    vendorContactId: number;
    userId: number;
    professionId: number;
    professionName: string;
    professionTypeId: number;
    professionTypeName: string;
}

export interface OfferJob {
    jobId: number;
    professionId: number | null;
}

export interface Systems {
    id: number;
    name: string;
}

export interface Facilities {
    id: number;
    name: string;
}

export interface ProfessionTypes {
    id: number;
    name: string;
}

export interface Expertise {
    id: number;
    name: string;
    abbreviation: string;
    sort: number;
    professionExpertiseCodes: any[];
    searchableVal?: string;
}

export interface OfferDetails {
    id: number;
    offerType: OfferType;
    candidateId: number;
    candidateName: string;
    candidateEmail: string;
    candidateProfessionId: number | null;
    statusId: number;
    contractTypeId: number;
    jobId: number;
    facilityId: number;
    facilityName: string;
    accountManagerName: string;
    positionTypeId: number;
    positionType: string;
    professionId: number;
    professionName: string;
    expertiseId: number;
    startDate: any;
    endDate: any;
    hours: any;
    hasShifts: boolean;
    shiftTypeId: number;
    shifts: number;
    shiftsAlt: any;
    hoursAlt: any;
    shiftTimeOfDayId: number;
    shiftAltTimeOfDayId: number;
    shiftM: number;
    shiftH: number;
    shiftToM: number;
    shiftToH: number;
    shiftAltM: any;
    shiftAltH: any;
    shiftAltToM: any;
    shiftAltToH: any;
    shiftDetails: string;
    hasRequestedTimeOff: boolean;
    requestedTimeOff: any;
    requestedTimeOffIsApproved: boolean;
    otherDetails: string;
    facilityRate: any;
    canEdit: boolean;
    // Agency specific
    vendorName: string;
    vendorRate: any;
    vendorId: number;
    vendorContactId: number;
    vendorContactName: string;
    vendorSubmittalId: number;
    // end agency specific
    status: string;
    isContractRequestStatus: boolean;
    canReactivateOffer: boolean;
    // Direct specific
    submittalJobId: number;
    contractLength: number;
    rtoApprovalStatus: RTOApprovalStatuses;
    isJobExtension: boolean;
    isJobApproved: boolean;
    recruiterBonus?: number;
    ayaPaidBonus?: number;
    isRemoteBooking?: boolean;
    isPatientFacing?: boolean;
    offerTimeOffDates?: Date[];
}

export interface Offer {
    id: number;
    offerType: OfferType;
    candidateId: number;
    candidateName: string;
    candidateEmail: string;
    statusId: OfferStatus;
    status: string;
    contractType: string;
    positionType: string;
    jobId: number;
    facilityName: string;
    accountManagerName: string;
    professionName: string;
    professionId: number;
    jobProfessionId: number;
    jobProfessionName: string;
    expertiseName: string;
    startDate: string;
    endDateText: string;
    shiftsText: string;
    shiftDetails: string;
    hasRequestedTimeOff: boolean;
    requestedTimeOffDateText: string;
    offerTimeOffDates?: Date[];
    requestedTimeOffIsApproved: boolean;
    facilityRate: number;
    otherDetails: string;
    canEdit: boolean;
    marginId: number | null;
    isSubcontractor: boolean;
    facilityId: number;
    // facility Address
    facilityAddress?: string;
    facilityCity?: string;
    facilityState?: string;
    facilityZip?: string;
    // Agency specific
    vendorName: string;
    vendorContactId: number;
    vendorContactName: string;
    vendorContactEmail: string;
    vendorRate: number;
    // End agency specific
    isContractRequestStatus: boolean;
    // end properties to remove
    eligibleStatuses: OfferStatus[];
    unitDescriptionFormId?: number;
    jobTitle: string;
    importedFromMarginId: number | null;
    marginImportDateUtc: Date | null;
    rtoApprovalStatus: RTOApprovalStatuses;
    contractRequestId: number;
    contractRequestStatus: string;
    recruiterBonus: number | null;
    canReactivateOffer: boolean | null;
    offerVerballyAccepted: boolean;
    readyForVerballyAccepted: boolean;
    jobEmploymentTypeCode?: EmploymentTypeCode;
    sampleMarginId?: number;
    unitName?: string;
    ayaPaidBonus?: number;
    isRemoteBooking?: boolean;
    isPatientFacing?: boolean;
}

export enum OfferType {
    Agency = 1,
    Direct = 2
}

export enum RTOApprovalStatuses {
    Approved = 1,
    PendingApproval = 2,
    NotApproved = 3
}

export interface OfferNotes {
    fullName: string;
    description: string;
    createdDate: Date;
    note: string;
    title: string;
    category: string;
}

export interface CandidateOfferClientNote {
    note: string;
    title: string;
    excludeNotification?: boolean;
}

export interface OfferJobDetails {
    jobId: number;
    facilityId: number;
    facilityName: string;
    professionId: number;
    expertiseId: number;
    startDate: any;
    endDate: any;
    hours: any;
    hasShifts: boolean;
    shiftTypeId: number;
    shifts: number;
    shiftsAlt: any;
    hoursAlt: any;
    shiftTimeOfDayId: number;
    shiftAltTimeOfDayId: number;
    shiftM: number;
    shiftH: number;
    shiftToM: number;
    shiftToH: number;
    shiftAltM: any;
    shiftAltH: any;
    shiftAltToM: any;
    shiftAltToH: any;
    facilityRate: number;
    vendorRate: number;
    candidateProfessionId?: number;
    candidateProfessionName: string;
}

export class JobIdClass {
    constructor(public id: number) {}
}

export interface IJobIdResponse {
    total: number;
    results: JobIdClass[];
}

export interface Note {
    id?: number;
    user?: string;
    role?: string;
    date?: string;
    note: string;
}

export interface Attachment {
    id: number;
    fileName: string;
    uploadDate: Date;
}

export interface OfferRates {
    facilityRate: number;
    vendorRate: number;
}

export interface OfferDetailsRecruiting extends Offer {
    contractTypeId: number;
    submittalId: number;
    vendorId: number;
    importedMarginId: number;
    importedMarginUpdatedDateUtc: number;
}

export interface DeclineReasons {
    categoryId?: number;
    categoryName?: string;
    id: number;
    name: string;
    subsReason?: ListItem[];
}

export interface DeclineReasonsPanel {
    categoryId?: number;
    categoryName?: string;
    id: number;
    name: string;
    subReasons?: ListItem[];
}
export interface VendorOptions {
    id: number;
    legalName: string;
    operatingName: string;
}

export interface DeclineOfferUpdate {
    reasonId: number;
    facilityId: number;
    facilityName: string;
    reasonText: string;
    subReasonIds: number[];
    payRate: number;
    payFreq: string;
    vendorId: number;
    vendorName: string;
    isExistingVendorForCandidate: boolean;
}
